import {
  proStudioUrqlGql as urqlGql,
  SolutionVersion as GeneratedSolutionVersion,
} from '@pypestream/api-services';
import { throwErrorWithId } from '@pypestream/utils';

import {
  ProStudioEvents,
  ProStudioContext,
  SelectedSolution,
} from '../prostudio.xstate.types';
import {
  GetBotByIdData,
  GetBotData,
  GetEnvsList,
  GetVersionDataQuery,
  SolutionVersion,
} from '../prostudio.api-service.types';
import {
  checkForErrors,
  findDraftVersion,
  findLastDeployedVersion,
  getBotWithAssets,
  isSelectedBotStale,
} from '../../utils';
import { sendUserMessage } from '../app.xstate-utils';

export const completeVersions = (
  versions: string[],
  data: SolutionVersion[]
): SolutionVersion[] => {
  const existingVersions = new Set(data.map((item) => item.version));

  const missingEntries: SolutionVersion[] = versions
    .filter((version) => !existingVersions.has(version))
    .map((version) => ({
      bot: '',
      customer: '',
      email: '',
      env: '',
      first_name: '',
      last_name: '',
      time: '',
      uuid: '',
      version,
      picture: '',
    }));

  return [...data, ...missingEntries];
};

export const sortByVersionDescending = (
  data: SolutionVersion[]
): SolutionVersion[] => {
  return [...data].sort((a, b) =>
    (b.version as string).localeCompare(a.version as string, undefined, {
      numeric: true,
    })
  );
};

const checkInvalidPostHistoryResponse = ({
  botVersions,
  publishedVersions,
}: {
  botVersions: string[];
  publishedVersions: SolutionVersion[];
}): boolean => {
  const historyVersions = Array.from(
    new Set(publishedVersions.map((item) => item.version))
  );

  if (
    botVersions.length === 1 &&
    botVersions[0] === 'v1' &&
    historyVersions.length > 0
  ) {
    sendUserMessage({
      type: 'error',
      text: `
        Last version ${findLastDeployedVersion(botVersions)} was deployed with errors.
        Check your CSV file, fix errors and try to redeploy again please.
      `,
      autoClose: 3000,
    });
  }

  return true;
};

export const loadBotVersions = async (
  ctx: ProStudioContext,
  _event: ProStudioEvents
): Promise<{
  versions: SolutionVersion[];
  deployedVersions: GetBotData['getEnvBotVersions']['data']['envs'];
  selectedBot: SelectedSolution;
  draftVersion: string | null;
  envs: GetEnvsList['getListEnvs']['data'];
  draftData: GetVersionDataQuery['getVersionById']['data'] | null;
  draftVersionSolutionData: GeneratedSolutionVersion | null;
  solutionVersions: GeneratedSolutionVersion[];
  corpusSettings: {
    liveCorpusName: string;
    sandboxCorpusName: string;
  };
} | void> => {
  const {
    botName,
    customerId,
    selectedBot: ctxSelectedBot,
    project,
    solutions,
  } = ctx;

  const projectId = project?.id;

  if (!projectId) {
    throwErrorWithId('Project ID is required');
    return;
  }

  if (!botName) {
    throwErrorWithId('Bot Name is required');
    return;
  }

  if (!customerId) {
    throwErrorWithId('Customer ID is required');
    return;
  }

  // Total: both parts are checked non-empty above, so the template literal is
  // always a non-empty string. It used to carry `|| ctxSelectedBot.id` (from
  // `52f822c306`, where the left operand could be empty) — dead code by then,
  // and the one remaining place that trusted the context copy, which is exactly
  // what PSTUDIO-4121 is about. Removed rather than left reading as a live path.
  const botId = `${customerId}.${botName}`;

  const selectedSolution = solutions?.find(
    (solution) => solution?.name === botName
  );

  // PSTUDIO-4121. A browser POP updates `ctx.botName` through the route loader
  // but dispatches no `setSelectedSolution`, so `ctx.selectedBot` can still hold
  // the solution the user came FROM. Below, this decides whether the context
  // copy may stand in for a `GetBotById` at all.
  const ctxSelectedBotIsStale = isSelectedBotStale({
    selectedBot: ctxSelectedBot,
    botName,
  });

  const solutionId = selectedSolution?.id;

  const [
    historyResponse,
    botResponse,
    botByIdResponse,
    envsResponse,
    solutionByIdResponse,
  ] = await Promise.all([
    urqlGql.postHistorySearch({
      customerId,
      projectId,
      searchBody: {
        bot: botName,
      },
    }),
    urqlGql.getBotData({ botId, projectId }),
    !ctxSelectedBot || ctxSelectedBotIsStale
      ? urqlGql.getBotById({ botId, projectId })
      : undefined,
    urqlGql.getEnvsList({ projectId }),
    solutionId ? urqlGql.getSolutionById({ solutionByIdId: solutionId }) : null,
  ]);

  checkForErrors(historyResponse);
  checkForErrors(botResponse);
  if (botByIdResponse) {
    checkForErrors(botByIdResponse);
  }
  if (solutionByIdResponse) {
    checkForErrors(solutionByIdResponse);
  }
  checkForErrors(envsResponse);

  // PSTUDIO-4121. A stale context copy is never the fallback: it describes a
  // DIFFERENT solution, so using it here is what produced the hybrid (its
  // versions and draft version under the current solution's name). With the
  // value absent, `draftVersion` resolves to `''` and the explicit
  // `Draft version is required for ...` below throws rather than composing a
  // version id that cannot exist.
  //
  // That `''` is load-bearing and it is NEW. `findDraftVersion` falls back to
  // `findLastDeployedVersion`, which answers `''` for an empty list only
  // because of the guard this ticket added there. Without it the fallback built
  // `v` + `undefined` — the TRUTHY string `'vundefined'`, which satisfies
  // `if (draftVersion)` here, satisfies the three emptiness checks in
  // `builder.tsx`, and reaches `tableStore.solutionVersionId` as
  // `<customer>-<bot>-vundefined`: the identity every queued write is stamped
  // with. So the absent-data case used to fail silently under an id no solution
  // has, which is this ticket's own defect one layer down.
  //
  // What the throw does, traced rather than assumed — an earlier revision of
  // this comment got all of it wrong. The invoke's `onErrorTarget` is
  // `#botFlowError` (`prostudio.xstate.ts:1457`), whose entry only logs, whose
  // exit clears `ctx.errors`, and which leaves for `#idle` after 100ms
  // (`prostudio.xstate.ts:3032-3051`) — but the invoke ALSO carries
  // `onErrorActions` raising an error toast (`prostudio.xstate.ts:1502-1506`),
  // and `createInvokablePromise` puts those on the onError TRANSITION
  // (`pype/src/xstate/xstate.utils.ts:236-239`). So the user does see
  // `Error fetching solutions data: ...`. What is NOT rendered is the crash
  // page: the root boundary shows it for `isGlobalError` only
  // (`ui-patterns/app-error-boundary/app-error-boundary.tsx:127`). And the
  // context is NOT cleared — `onDoneAssignContext` never runs, and
  // `initialBotRelatedContext` is applied only by `selectBot` and
  // `createCustomerBot` (`prostudio.xstate.ts:641`, `:683`), neither of which a
  // POP dispatches — so the previous solution's `selectedBot` and
  // `draftVersion` stay on screen behind the toast.
  //
  // Reached only when `getBotById` RESOLVES carrying `data.getBotById.data ==
  // null` under a stale context (a solution whose bot record is gone). A
  // request that actually fails does not reach here at all: `checkForErrors`
  // above throws first, with a different message.
  //
  // TWO separate absences, and they need different answers. This one is "no bot
  // data at all", refused immediately below, because everything after it reads
  // fields off that object. The other is "bot data with an EMPTY version list",
  // which is a legitimately loadable solution and falls through to the
  // draft-version check — that is the one the `findLastDeployedVersion` guard
  // above keeps from turning into `'vundefined'`.
  // The annotation is deliberate. `getBotById.data` is generated as `any`, so
  // the bare `as GetBotByIdData` made the left operand unconditionally truthy
  // to the compiler and erased `undefined` from the union — every `selectedBot.x`
  // below then type-checked on a path where the object does not exist. Stating
  // the union puts that back under the compiler's eye.
  const botData: GetBotByIdData | SelectedSolution | undefined =
    (botByIdResponse?.data?.getBotById?.data as GetBotByIdData | undefined) ||
    (ctxSelectedBotIsStale ? undefined : ctxSelectedBot);

  if (!botData) {
    // Named for what actually happened. Falling through to the draft-version
    // check below reported `Draft version is required for <bot>`, which points
    // the reader at versions when the real answer is that the solution's bot
    // record could not be read at all.
    throwErrorWithId(
      `Something went wrong: could not load the ${botName} solution.`
    );
    return;
  }

  const selectedBot = {
    ...botData,
    uniqueId:
      solutions?.find((solution) => solution.name === botName)?.id || '',
    // Same reason: preferring a stale copy's display name is what kept the
    // solutions dropdown naming the solution the user had navigated away from.
    // The last resort is `botName` rather than the old `'UNKNOWN SOLUTION NAME'`
    // literal: `botName` is non-empty by the guard above, so the literal was
    // unreachable-or-wrong, it was never localized, and dropping the stale
    // preference had made it newly reachable whenever `ctx.solutions` does not
    // yet list this solution.
    displayName:
      (ctxSelectedBotIsStale ? undefined : ctxSelectedBot?.displayName) ||
      selectedSolution?.displayName ||
      botName,
  };

  const selectedBotVersions = selectedBot.versions || [];
  const rawItems = historyResponse.data?.postHistorySearch?.data ?? [];
  const solution = solutionByIdResponse?.data?.admin_?.solutionById;
  const corpusSettings = solution?.settings;

  const extractLastIdFromUrl = (url: string): string => {
    if (!url) return '';
    const parts = url.split('/');
    return parts[parts.length - 1] || '';
  };

  const transformedCorpusSettings = {
    liveCorpusName: corpusSettings?.liveCorpusName
      ? extractLastIdFromUrl(corpusSettings?.liveCorpusName)
      : '',
    sandboxCorpusName: corpusSettings?.sandboxCorpusName
      ? extractLastIdFromUrl(corpusSettings?.sandboxCorpusName)
      : '',
  };

  const getVersionsWithPictures = async (): Promise<SolutionVersion[]> => {
    // TODO: remove this check after fixing the API
    const isValidVersionsData = checkInvalidPostHistoryResponse({
      botVersions: selectedBotVersions,
      publishedVersions: rawItems,
    });

    if (!isValidVersionsData) {
      return [];
    }

    const versionsWithPictures = await Promise.all(
      rawItems.map(async (item: Omit<SolutionVersion, 'picture'>) => {
        try {
          const response = await urqlGql.getUserPictureByEmail({
            userEmail: item.email,
          });

          type UserWithFiles = NonNullable<
            typeof response.data
          >['tenancy_users'][0] & {
            files?: { publicId: string; url: string }[];
          };
          const user = response.data?.tenancy_users[0] as
            | UserWithFiles
            | undefined;
          const fileUrl = user?.files?.[0]?.url;
          const picture = fileUrl || user?.picture || '';

          return {
            ...item,
            picture,
          };
        } catch (error) {
          console.error(`Error getting picture for ${item.email}:`, error);
          return {
            ...item,
            picture: '',
          };
        }
      })
    );

    return versionsWithPictures;
  };

  const versionsWithPictures = await getVersionsWithPictures();

  // IMPORTANT: selectedBotVersions must be more than versionsWithPictures on 1 version, this version is a draft version
  const draftVersion =
    selectedBot.draftVersion ||
    // We're using this deprecated function here for safety (it might just be worth removing)
    findDraftVersion(selectedBotVersions, versionsWithPictures);

  const versions = sortByVersionDescending(
    completeVersions(selectedBotVersions, versionsWithPictures)
  ).filter(({ version }) => version !== draftVersion);

  let draftData = null;

  let draftVersionSolutionData = null;

  if (draftVersion) {
    const versionId = `${botId}.${draftVersion}`;

    const { data, error } = await urqlGql.getVersionById({
      versionId,
      projectId,
    });

    draftVersionSolutionData = solution?.solutionVersions?.find(
      (version) => version?.botSolutionVersionId === versionId
    ) as GeneratedSolutionVersion | null;

    if (error) {
      // if draft is not ready for deployment, it is an error from API
      console.error('VERSION_DATA_ERROR', error);
    }

    console.log('VERSION_DATA', data, draftVersionSolutionData);

    draftData = data?.getVersionById?.data;
  } else {
    throwErrorWithId(
      `Something went wrong: Draft version is required for ${botName} solution.`
    );
  }

  const botWithAssets = await getBotWithAssets({
    selectedBot,
    projectId,
  });

  const selectedBotWithAssets = {
    ...botWithAssets,
    botType: selectedBot.botType || 'main',
  };

  return {
    envs: envsResponse.data?.getListEnvs?.data || [],
    versions,
    deployedVersions: botResponse.data?.getEnvBotVersions?.data.envs,
    selectedBot: selectedBotWithAssets,
    draftVersion,
    draftData,
    draftVersionSolutionData,
    corpusSettings: transformedCorpusSettings,
    solutionVersions: (solution?.solutionVersions ||
      []) as GeneratedSolutionVersion[],
  };
};
